import { AdvancedFilterConfig, FilterOperator } from '../types/filter';
import { RowConfig, ColumnConfig } from '@/shared/types/table';
import { getRelativeDateRange, isDateString, parseDate } from './tableFilterDateHelpers';

/**
 * Función auxiliar para comparar fechas
 */
const compareDates = (value: unknown, filterValue: unknown, operator: string): boolean => {
  const valueDate = parseDate(String(value));
  const filterDate = parseDate(String(filterValue));

  // Comparar timestamps
  const valueTime = valueDate.getTime();
  const filterTime = filterDate.getTime();

  switch (operator) {
    case 'equals':
      return valueTime === filterTime;
    case 'notEquals':
      return valueTime !== filterTime;
    case 'gt':
    case 'afterDate':
      return valueTime > filterTime;
    case 'gte':
      return valueTime >= filterTime;
    case 'lt':
    case 'beforeDate':
      return valueTime < filterTime;
    case 'lte':
      return valueTime <= filterTime;
    default:
      return false;
  }
};

/**
 * Verifica si una fecha está dentro de un rango relativo
 */
const isDateInRelativeRange = (value: unknown, operator: string): boolean => {
  if (!isDateString(value)) return false;

  const valueDate = parseDate(String(value));
  const { start, end } = getRelativeDateRange(operator);

  const valueTime = valueDate.getTime();
  const startTime = start.getTime();
  const endTime = end.getTime();

  return valueTime >= startTime && valueTime <= endTime;
};

/**
 * Aplica un filtro individual a un valor
 */
export const applyFilterOperator = (
  value: unknown,
  operator: FilterOperator,
  filterValue: unknown,
  filterValue2?: unknown
): boolean => {
  // Manejar valores nulos/undefined
  const isValueEmpty = value === null || value === undefined || value === '';

  switch (operator) {
    case 'isEmpty':
      return isValueEmpty;

    case 'isNotEmpty':
      return !isValueEmpty;

    case 'equals':
      if (
        isValueEmpty &&
        (filterValue === null || filterValue === undefined || filterValue === '')
      ) {
        return true;
      }
      // Comparar fechas si ambos son strings de fecha
      if (isDateString(value) && isDateString(filterValue)) {
        return compareDates(value, filterValue, 'equals');
      }
      return String(value).toLowerCase() === String(filterValue).toLowerCase();

    case 'notEquals':
      if (
        isValueEmpty &&
        (filterValue === null || filterValue === undefined || filterValue === '')
      ) {
        return false;
      }
      // Comparar fechas si ambos son strings de fecha
      if (isDateString(value) && isDateString(filterValue)) {
        return compareDates(value, filterValue, 'notEquals');
      }
      return String(value).toLowerCase() !== String(filterValue).toLowerCase();

    case 'in':
      // Para operador "in" (Es uno de), verificar si el valor está en el array
      if (isValueEmpty) return false;
      if (!Array.isArray(filterValue)) return false;

      // Si el valor es un array (como tags o avatars), verificar si algún elemento está en filterValue
      if (Array.isArray(value)) {
        return value.some((v) => {
          const strValue =
            typeof v === 'object' && v !== null
              ? v.name || v.label || v.value || v.id || JSON.stringify(v)
              : String(v);
          return filterValue.some(
            (fv) => String(fv).toLowerCase() === String(strValue).toLowerCase()
          );
        });
      }

      // Si el valor es un objeto, intentar obtener una propiedad común
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const objValue = value as Record<string, unknown>;
        const strValue =
          (objValue.name && String(objValue.name)) ||
          (objValue.label && String(objValue.label)) ||
          (objValue.value && String(objValue.value)) ||
          (objValue.id && String(objValue.id)) ||
          JSON.stringify(value);
        return filterValue.some(
          (fv) => String(fv).toLowerCase() === String(strValue).toLowerCase()
        );
      }

      // Si es un valor simple, verificar si está en el array
      return filterValue.some((fv) => String(fv).toLowerCase() === String(value).toLowerCase());

    case 'contains':
      if (isValueEmpty) return false;
      return String(value).toLowerCase().includes(String(filterValue).toLowerCase());

    case 'notContains':
      if (isValueEmpty) return true;
      return !String(value).toLowerCase().includes(String(filterValue).toLowerCase());

    case 'startsWith':
      if (isValueEmpty) return false;
      return String(value).toLowerCase().startsWith(String(filterValue).toLowerCase());

    case 'endsWith':
      if (isValueEmpty) return false;
      return String(value).toLowerCase().endsWith(String(filterValue).toLowerCase());

    case 'gt':
      if (isValueEmpty) return false;
      // Comparar fechas si ambos son strings de fecha
      if (isDateString(value) && isDateString(filterValue)) {
        return compareDates(value, filterValue, 'gt');
      }
      return Number(value) > Number(filterValue);

    case 'gte':
      if (isValueEmpty) return false;
      // Comparar fechas si ambos son strings de fecha
      if (isDateString(value) && isDateString(filterValue)) {
        return compareDates(value, filterValue, 'gte');
      }
      return Number(value) >= Number(filterValue);

    case 'lt':
      if (isValueEmpty) return false;
      // Comparar fechas si ambos son strings de fecha
      if (isDateString(value) && isDateString(filterValue)) {
        return compareDates(value, filterValue, 'lt');
      }
      return Number(value) < Number(filterValue);

    case 'lte':
      if (isValueEmpty) return false;
      // Comparar fechas si ambos son strings de fecha
      if (isDateString(value) && isDateString(filterValue)) {
        return compareDates(value, filterValue, 'lte');
      }
      return Number(value) <= Number(filterValue);

    case 'between':
      if (isValueEmpty) return false;
      // Comparar fechas si son strings de fecha
      if (isDateString(value) && isDateString(filterValue) && isDateString(filterValue2)) {
        const valueDate = parseDate(String(value));
        const filterDate1 = parseDate(String(filterValue));
        const filterDate2 = parseDate(String(filterValue2));

        const valueTime = valueDate.getTime();
        const minTime = Math.min(filterDate1.getTime(), filterDate2.getTime());
        const maxTime = Math.max(filterDate1.getTime(), filterDate2.getTime());

        return valueTime >= minTime && valueTime <= maxTime;
      }
      // Para números
      const numValue = Number(value);
      const min = Math.min(Number(filterValue), Number(filterValue2));
      const max = Math.max(Number(filterValue), Number(filterValue2));
      return numValue >= min && numValue <= max;

    // Operadores específicos de fecha
    case 'afterDate':
      if (isValueEmpty) return false;
      if (!isDateString(value) || !isDateString(filterValue)) return false;
      return compareDates(value, filterValue, 'afterDate');

    case 'beforeDate':
      if (isValueEmpty) return false;
      if (!isDateString(value) || !isDateString(filterValue)) return false;
      return compareDates(value, filterValue, 'beforeDate');

    // Operadores de fechas relativas
    case 'thisMonth':
    case 'lastMonth':
    case 'thisYear':
    case 'lastYear':
      if (isValueEmpty) return false;
      return isDateInRelativeRange(value, operator);

    default:
      return true;
  }
};

/**
 * Obtiene el valor de una columna desde un row según el accessor
 */
export const getColumnValue = <T>(
  row: RowConfig<T>,
  columnId: string,
  columns: ColumnConfig<T>[]
): unknown => {
  const column = columns.find((col) => col.id === columnId);
  if (!column) return undefined;

  // Si tiene accessor personalizado
  if (column.accessor) {
    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }
    return (row as Record<string, unknown>)[column.accessor as string];
  }

  // Por defecto, usar el id de la columna como key
  return (row as Record<string, unknown>)[columnId];
};

/**
 * Aplica todos los filtros a un array de rows con soporte para compuertas lógicas (AND/OR)
 */
export const applyFilters = <T>(
  rows: RowConfig<T>[],
  filters: AdvancedFilterConfig[],
  columns: ColumnConfig<T>[]
): RowConfig<T>[] => {
  if (filters.length === 0) return rows;

  return rows.filter((row) => {
    if (filters.length === 1) {
      // Si solo hay un filtro, evaluarlo directamente
      const value = getColumnValue(row, filters[0].columnId, columns);
      return applyFilterOperator(value, filters[0].operator, filters[0].value, filters[0].value2);
    }

    // Evaluar múltiples filtros con compuertas lógicas
    let result = false;

    for (let i = 0; i < filters.length; i++) {
      const filter = filters[i];
      const value = getColumnValue(row, filter.columnId, columns);
      const filterResult = applyFilterOperator(value, filter.operator, filter.value, filter.value2);

      if (i === 0) {
        // Primer filtro: establecer el resultado inicial
        result = filterResult;
      } else {
        // Filtros subsecuentes: aplicar la compuerta lógica del filtro anterior
        const previousFilter = filters[i - 1];
        const logicOperator = previousFilter.logicOperator || 'and';

        if (logicOperator === 'and') {
          result = result && filterResult;
        } else if (logicOperator === 'or') {
          result = result || filterResult;
        }
      }
    }

    return result;
  });
};

/**
 * Hook para validar si hay filtros activos
 */
export const hasActiveFilters = (filters: AdvancedFilterConfig[]): boolean => {
  return filters.length > 0;
};

/**
 * Obtiene un resumen de los filtros activos
 */
export const getFiltersSummary = (filters: AdvancedFilterConfig[]): string => {
  if (filters.length === 0) return 'Sin filtros';
  if (filters.length === 1) return '1 filtro activo';
  return `${filters.length} filtros activos`;
};
